import Util from "@/js/common/Util";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ko";
import React from "react";
import AppContext from "../../AppContext";

export enum DatePickerInitType {
  MONTH_AGO = "month-ago",
  MONTH_FIRST = "month-first",
  MONTH_LAST = "month-last",
  CURRENT_DATE = "current-dt",
  MONTH_PREV_DATE = "month-prev-dt",
}

interface DatePickerBoxProps {
  width?: number;
  height: number;
  fontSize: number;
  fontFamily?: string;
  color?: string;
  letterSpacing?: number;
  textAlign: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  value?: string;
  initType?: DatePickerInitType;
  className?: string;
  bgColor?: string;
  borderColor?: string;
  borderOnColor?: string;
  borderRadius?: number;
  format?: "YYYY. MM. DD" | "YYYY-MM-DD" | "YYYY/MM/DD" | "YYYY년 MM월 DD일";
  calendarThemeColor: string;
}

const DatePickerTheme = (theme: any, calendarThemeColor: string) =>
  createTheme({
    ...theme,
    components: {
      MuiPickersDay: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: "white",
              backgroundColor: calendarThemeColor + " !important",
            },
            "&:focus": {
              border: `1px solid ${calendarThemeColor}`,
            },
          },
          today: {
            ":not(.Mui-selected)": {
              border: `1px solid ${calendarThemeColor}`,
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            "&.Mui-selected": {
              color: "white",
              backgroundColor: AppContext.themeColor + " !important",
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            "&.Mui-selected": {
              color: "white",
              backgroundColor: AppContext.themeColor + " !important",
            },
          },
        },
      },
    },
  });

const getInitValue = (type?: DatePickerInitType): Dayjs | null => {
  switch (type) {
    case DatePickerInitType.MONTH_AGO:
      return dayjs().subtract(1, "month");
    case DatePickerInitType.MONTH_FIRST:
      return dayjs().startOf("month");
    case DatePickerInitType.MONTH_LAST:
      return dayjs().endOf("month");
    case DatePickerInitType.CURRENT_DATE:
      return dayjs();
    case DatePickerInitType.MONTH_PREV_DATE:
      return dayjs().subtract(1, "day");
    default:
      return null;
  }
};

/** 입력 문자열을 format 기준(strict)으로 파싱 */
const parseInputDate = (value: string, format: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, format, true); // strict
  return parsed.isValid() ? parsed : null;
};

const DatePickerBox = ({
  width,
  height,
  fontSize,
  fontFamily,
  color,
  letterSpacing,
  textAlign,
  disabled,
  onChange,
  value,
  initType,
  className,
  bgColor,
  borderColor,
  borderOnColor,
  borderRadius,
  format,
  calendarThemeColor,
}: DatePickerBoxProps) => {
  const theme = createTheme();
  const datePickerTheme = DatePickerTheme(theme, calendarThemeColor);

  const [dateValue, setDateValue] = React.useState<Dayjs | null>(null);
  const [focusState, setFocusState] = React.useState<boolean>(false);
  const [hoverState, setHoverState] = React.useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  /** 키보드/블러 공통 처리 */
  const handleManualInput = React.useCallback(
    (inputValue: string) => {
      const fmt = format ? format : "YYYY-MM-DD";
      const parsed = parseInputDate(inputValue, fmt);

      if (!parsed) {
        setDateValue(null);
        onChange("");
        return;
      }

      setDateValue(parsed);
      onChange(Util.dateToString(parsed, "YYYYMMDD"));
    },
    [format, onChange]
  );

  /** Enter / Blur 시 날짜 인식 */
  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleManualInput(input.value);
      }
    };

    const blurHandler = () => {
      handleManualInput(input.value);
    };

    input.addEventListener("keydown", keyDownHandler);
    input.addEventListener("blur", blurHandler);

    return () => {
      input.removeEventListener("keydown", keyDownHandler);
      input.removeEventListener("blur", blurHandler);
    };
  }, [handleManualInput]);

  /** 외부 value / initType 변경 동기화 */
  React.useEffect(() => {
    if (Util.isNotEmpty(value)) {
      setDateValue(dayjs(value));
    } else {
      setDateValue(getInitValue(initType));
    }
  }, [value, initType]);

  return (
    <div
      className={"bsBdb " + (className ?? "")}
      style={{ width: width ? width : "100%" }}
      onMouseOver={() => setHoverState(true)}
      onMouseOut={() => setHoverState(false)}
    >
      <ThemeProvider theme={datePickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <DemoContainer
            components={["DatePicker"]}
            sx={{
              paddingTop: 0,
              overflow: "inherit",
              height: height + "px",
              ".MuiOutlinedInput-root": {
                fontFamily: fontFamily ? fontFamily : "fm-b",
                color: color ? color : "#666",
                letterSpacing: letterSpacing ? letterSpacing : -0.5,
                background: Util.isNotEmpty(bgColor) ? bgColor : "transparent",
                border: "none",
                borderRadius: borderRadius + "px",
                width: "100%",
              },
              ".MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: (color ? color : "#666") + " !important",
                color: (color ? color : "#666") + " !important",
                opacity: "1 !important",
              },
              ".MuiTextField-root": { width: "100%", minWidth: "0 !important" },
              ".MuiFormLabel-root": {
                fontSize: 12,
                fontFamily: fontFamily ? fontFamily : "fm-b",
              },
              ".MuiInputBase-root": {
                lineHeight: 0,
                letterSpacing: 0,
                height: height,
                paddingRight: 0,
              },
              ".MuiInputBase-input": {
                fontSize: fontSize + "px",
                padding: "0 22px 0 10px",
                border: borderColor
                  ? "1px solid " + (focusState || hoverState ? borderOnColor : borderColor)
                  : "",
                borderRadius: borderRadius + "px",
                height: "100%",
                boxSizing: "border-box",
                animation: "none",
                fontFamily: fontFamily ? fontFamily : "fm-b",
                textAlign: textAlign,
                letterSpacing: -1,
              },
              ".MuiInputBase-input:focus": {
                borderColor: focusState ? borderOnColor : disabled ? "#dddddd" : borderOnColor,
              },
              ".MuiOutlinedInput": { padding: 0 },
              ".MuiOutlinedInput-notchedOutline": {
                borderWidth: "1 !important",
                padding: 0,
                display: "none",
              },
              ".MuiInputAdornment-root": { position: "absolute", right: 5 },
              ".MuiIconButton-root": { padding: 0, margin: 0 },
            }}
          >
            <DatePicker
              format={format ? format : "YYYY-MM-DD"}
              value={dateValue}
              views={["year", "month", "day"]}
              onOpen={() => setFocusState(true)}
              onClose={() => setFocusState(false)}
              onAccept={(data) => {
                if (!data) {
                  setDateValue(null);
                  onChange("");
                  return;
                }
                setDateValue(data);
                onChange(Util.dateToString(data, "YYYYMMDD"));
              }}
              disabled={disabled}
              sx={{
                ".MuiInputBase-input::placeholder": { color: "#999999" },
                ".MuiPickersDay-root": { fontSize: "1.2rem" },
                ".MuiPickersCalendarHeader-label": { fontSize: "1.5rem" },
                ".MuiPickersCalendarHeader-switchViewButton": { fontSize: "1.2rem" },
                ".MuiPickersSlideTransition-root": { fontSize: "1.2rem" },
              }}
              slots={{
                openPickerIcon: () => (
                  <img
                    src={Util.getImgSrc(
                      "common/icon_calendar_month_" + (disabled ? "in" : "") + "active20.png"
                    )}
                    alt="calendar"
                  />
                ),
              }}
              slotProps={{
                textField: {
                  inputRef: inputRef,
                },
                popper: {
                  sx: { zIndex: 999999 },
                },
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
};

export default DatePickerBox;
